/**
 * The canonical error model shared by REST and WebSocket (protocol.md §10). Mirror of
 * TeamSim.Contracts/ErrorCodes.cs + ApiError.
 */

/** Canonical error codes (extensible; clients MUST handle unknown codes generically). */
export const ErrorCodes = {
  AuthInvalid: 'AUTH_INVALID',
  AuthExpired: 'AUTH_EXPIRED',
  UpgradeRequired: 'UPGRADE_REQUIRED',
  ProtocolUnsupported: 'PROTOCOL_UNSUPPORTED',
  NotMember: 'NOT_MEMBER',
  PowerDenied: 'POWER_DENIED',
  RightsDenied: 'RIGHTS_DENIED',
  GateClosed: 'GATE_CLOSED',
  QuorumRequired: 'QUORUM_REQUIRED',
  VersionConflict: 'VERSION_CONFLICT',
  SessionFull: 'SESSION_FULL',
  SessionClosed: 'SESSION_CLOSED',
  RoleTaken: 'ROLE_TAKEN',
  ValidationFailed: 'VALIDATION_FAILED',
  RateLimited: 'RATE_LIMITED',
  BudgetExhausted: 'BUDGET_EXHAUSTED',
  DependencyDown: 'DEPENDENCY_DOWN',
  Internal: 'INTERNAL',
} as const;

export type ErrorCode = (typeof ErrorCodes)[keyof typeof ErrorCodes] | (string & {});

/** The canonical error shape (protocol.md §10). `traceId` is (almost) always present server-side. */
export interface ApiError {
  code: ErrorCode;
  /** Localized, player-facing, safe to display. */
  message: string;
  retryable?: boolean;
  retryAfterMs?: number | null;
  /** Machine-readable, stable. */
  details?: unknown;
  /** Correlates to server logs (logging §7). */
  traceId?: string | null;
}

/** A typed `Error` carrying the canonical {@link ApiError}. Thrown/rejected across REST and WS. */
export class ProtocolError extends Error {
  readonly code: ErrorCode;
  readonly retryable: boolean;
  readonly retryAfterMs: number | null;
  readonly details: unknown;
  readonly traceId: string | null;
  /** HTTP status when this came from a REST call, else null. */
  readonly httpStatus: number | null;

  constructor(error: ApiError, httpStatus: number | null = null) {
    super(error.message || error.code);
    this.name = 'ProtocolError';
    this.code = error.code;
    this.retryable = error.retryable ?? false;
    this.retryAfterMs = error.retryAfterMs ?? null;
    this.details = error.details;
    this.traceId = error.traceId ?? null;
    this.httpStatus = httpStatus;
  }

  toApiError(): ApiError {
    return {
      code: this.code,
      message: this.message,
      retryable: this.retryable,
      retryAfterMs: this.retryAfterMs,
      details: this.details,
      traceId: this.traceId,
    };
  }
}

export function isApiError(value: unknown): value is ApiError {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as Record<string, unknown>).code === 'string'
  );
}
